import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../../components/Layout";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import { SELLER_SECTORS, CUSTOM_SECTOR, SPAIN_REGIONS, REST_OF_WORLD } from "../../lib/taxonomy";
import { t } from "../../lib/i18n";

export default function NewCompany() {
  const [form, setForm] = useState({
    name: "",
    sector: "",
    custom_sector: "",  // solo aplica si sector === CUSTOM_SECTOR
    location: "Madrid",   // CCAA o REST_OF_WORLD
    foreign_country: "",  // solo aplica si location === REST_OF_WORLD
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.sector || !form.location) {
      toast.error(t("Completa todos los campos"));
      return;
    }
    const sector = form.sector === CUSTOM_SECTOR ? form.custom_sector.trim() : form.sector;
    if (form.sector === CUSTOM_SECTOR && sector.length < 2) {
      toast.error(t("Escribe el sector de tu empresa"));
      return;
    }
    if (form.location === REST_OF_WORLD && !form.foreign_country.trim()) {
      toast.error(t("Indica el país de la empresa"));
      return;
    }
    // El backend guarda `country` como el valor final (CCAA exacta o REST_OF_WORLD).
    // El país libre del extranjero se guarda como `foreign_country` para mostrarlo.
    const payload = {
      name: form.name,
      sector,
      country: form.location,
      foreign_country: form.location === REST_OF_WORLD ? form.foreign_country.trim() : null,
    };
    setLoading(true);
    try {
      const r = await api.post("/companies", payload);
      toast.success(t("Empresa creada"));
      navigate(`/seller/company/${r.data.company_id}/questionnaire`);
    } catch {
      toast.error(t("Error creando empresa"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-6 py-16">
        <span className="font-mono-label text-gold">{t("Nuevo registro")}</span>
        <h1 className="font-serif-display text-4xl md:text-5xl mt-3 mb-3">{t("Cuéntanos lo básico")}</h1>
        <p className="text-muted-foreground mb-10">
          {t("Empezamos por lo más general. Conforme avances, desbloquearás campos para ir validando tu empresa.")}
        </p>

        <form onSubmit={submit} className="hairline p-8 bg-card space-y-6">
          <Field label={t("Nombre de la empresa")}>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder={t("p. ej. Acme Industries SL")}
              className="rounded-none border-border focus:ring-1 focus:ring-[#1E3A5F]"
              data-testid="new-company-name"
            />
          </Field>

          <Field label={t("Sector")}>
            <Select value={form.sector} onValueChange={(v) => setForm({ ...form, sector: v })}>
              <SelectTrigger className="rounded-none" data-testid="new-company-sector">
                <SelectValue placeholder={t("Selecciona un sector")} />
              </SelectTrigger>
              <SelectContent className="max-h-80">
                {SELLER_SECTORS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                <SelectGroupLabel>{t("¿No está en la lista?")}</SelectGroupLabel>
                <SelectItem value={CUSTOM_SECTOR} data-testid="new-company-sector-custom-option">{t("Otro · escribir mi sector")}</SelectItem>
              </SelectContent>
            </Select>
          </Field>

          {form.sector === CUSTOM_SECTOR && (
            <Field label={t("Escribe tu sector")}>
              <Input
                value={form.custom_sector}
                onChange={(e) => setForm({ ...form, custom_sector: e.target.value })}
                placeholder={t("p. ej. Óptica, Gestión de residuos, Estética avanzada…")}
                maxLength={60}
                className="rounded-none"
                data-testid="new-company-custom-sector"
              />
              <p className="text-xs text-muted-foreground">{t("Así lo verán searchers e inversores en el marketplace.")}</p>
            </Field>
          )}

          <Field label={t("Ubicación (CCAA o resto del mundo)")}>
            <Select value={form.location} onValueChange={(v) => setForm({ ...form, location: v })}>
              <SelectTrigger className="rounded-none" data-testid="new-company-location">
                <SelectValue placeholder={t("Selecciona ubicación")} />
              </SelectTrigger>
              <SelectContent>
                <SelectGroupLabel>{t("España · Comunidades Autónomas")}</SelectGroupLabel>
                {SPAIN_REGIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                <SelectGroupLabel>{t("Internacional")}</SelectGroupLabel>
                <SelectItem value={REST_OF_WORLD}>{REST_OF_WORLD}</SelectItem>
              </SelectContent>
            </Select>
          </Field>

          {form.location === REST_OF_WORLD && (
            <Field label={t("País de la empresa")}>
              <Input
                value={form.foreign_country}
                onChange={(e) => setForm({ ...form, foreign_country: e.target.value })}
                placeholder={t("p. ej. Portugal, México, Colombia…")}
                className="rounded-none"
                data-testid="new-company-foreign-country"
              />
            </Field>
          )}

          <Button
            type="submit"
            disabled={loading}
            className="rounded-none w-full bg-[#1E3A5F] hover:bg-[#2B4C78] text-bone h-12"
            data-testid="new-company-submit"
          >
            {loading ? t("Creando…") : t("Crear empresa y continuar")}
          </Button>
        </form>
      </div>
    </Layout>
  );
}

const Field = ({ label, children }) => (
  <div className="space-y-2">
    <Label className="font-mono-label text-muted-foreground">{label}</Label>
    {children}
  </div>
);

const SelectGroupLabel = ({ children }) => (
  <div className="px-2 py-1.5 font-mono-label text-muted-foreground" style={{ fontSize: "0.6rem" }}>
    {children}
  </div>
);
